import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCartContinuePricing } from 'features/cart/hooks/use-cart-continue/use-cart-continue-pricing';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { calculateCartSubTotal } from 'features/cart/utils';
import { useStoreContext } from 'features/store';

import { IPriceOrderParams, usePriceOrder } from 'hooks/price-order';
import { useCart } from 'hooks/use-cart';
import { useAuthContext } from 'state/auth';
import { getPriceOrderCustomerName } from 'state/cart/customer-name';
import { useSanitizedRewards } from 'state/cart/hooks/use-sanitized-rewards';
import { actions, useAppDispatch } from 'state/global-state';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useTransformedAppliedOffers } from 'state/loyalty/hooks/use-transformed-applied-offers';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { getServiceModeFromLS } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';
import { isPhoneNumberValidSimple } from 'utils/form';
import noop from 'utils/noop';
import { isDelivery } from 'utils/service-mode';

import { useCheckoutContext } from './use-checkout-context';

type PendingCheckoutReprice = {
  mode: ServiceMode;
  needsLocalReprice: boolean;
  waitForStoreReprice: boolean;
};

/**
 * Checkout helper to (re)price the current order using the same PriceOrderV2
 * flow used on cart continue for minimal cart.
 *
 * Store-switch repricing waits for unified-menu readiness, updates the live minimal cart,
 * then invokes shared pricing so checkout can refetch the newly priced order.
 */
export const useCheckoutPriceOrder = () => {
  const enableMinimalCartUi = useMinimalCartUi();
  const { isStoreOpenAndAvailable, store } = useStoreContext();
  const {
    deliveryAddress,
    deliveryInstructions,
    orderPhoneNumber,
    setCheckoutRequestedSubtotalCents,
  } = useOrderContext();
  const { serverOrder, vehicleDescription } = useCheckoutContext();
  const { user } = useAuthContext();
  const dispatch = useAppDispatch();
  const { locale: customerLocale } = useLocale();
  const { cartEntries: minimalCartEntries, repriceMinimalCartEntries } = useMinimalCart();
  const {
    cartEntries: legacyCartEntries,
    calculateCartTotalWithDiscount,
    repriceCartEntries,
  } = useCart();
  const legacyPricedCartEntries = useMemo(
    () => (enableMinimalCartUi ? [] : repriceCartEntries(legacyCartEntries)),
    [enableMinimalCartUi, legacyCartEntries, repriceCartEntries]
  );
  const rewardsApplied = useSanitizedRewards({ cartEntries: legacyPricedCartEntries });
  const validAppliedOffers = useTransformedAppliedOffers();
  const { allPricingDependenciesAreSettled } = useMenuContext();
  const isFastLaneEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAST_LANE_DRIVE_THRU);
  const {
    indexedMenu,
    loadedStoreNumber,
    isLoading: isUnifiedMenuLoading,
  } = useUnifiedMenuContext();
  const { startPricing: startMinimalCartPricing } = useCartContinuePricing({
    cartEntries: minimalCartEntries,
    onSuccess: noop,
  });
  const { priceOrder: priceLegacyOrder, setIsRepricing: setIsLegacyRepricing } = usePriceOrder({
    cartEntries: legacyPricedCartEntries,
    enabled: !enableMinimalCartUi,
  });
  const [
    pendingCheckoutReprice,
    setPendingCheckoutReprice,
  ] = useState<PendingCheckoutReprice | null>(null);
  const currentStoreNumber = store?.number ?? null;
  const isStoreMenuReady = Boolean(
    currentStoreNumber &&
      loadedStoreNumber === currentStoreNumber &&
      indexedMenu.isLoaded() &&
      !isUnifiedMenuLoading
  );
  const {
    cartTotal: legacyCartTotal,
    isCartTotalNegative: isLegacyCartTotalNegative,
  } = useMemo(() => calculateCartTotalWithDiscount(legacyPricedCartEntries), [
    calculateCartTotalWithDiscount,
    legacyPricedCartEntries,
  ]);
  const legacyRequestedAmountCents = Math.max(0, legacyCartTotal);
  const legacyPriceOrderStoreParam = useMemo(() => {
    const priceOrderStoreParam: IPriceOrderParams['store'] = {
      number: store.number,
      posRestaurantId: store.posRestaurantId,
      pos: {
        vendor: store.pos?.vendor,
      },
      vatNumber: store.vatNumber,
      physicalAddress: {
        address1: store.physicalAddress?.address1,
        address2: store.physicalAddress?.address2,
        city: store.physicalAddress?.city,
        country: store.physicalAddress?.country,
        postalCode: store.physicalAddress?.postalCode,
        stateProvince: store.physicalAddress?.stateProvince,
      },
      phoneNumber: store.phoneNumber,
    };

    return priceOrderStoreParam;
  }, [
    store.number,
    store.phoneNumber,
    store.physicalAddress?.address1,
    store.physicalAddress?.address2,
    store.physicalAddress?.city,
    store.physicalAddress?.country,
    store.physicalAddress?.postalCode,
    store.physicalAddress?.stateProvince,
    store.pos?.vendor,
    store.posRestaurantId,
    store.vatNumber,
  ]);

  const getCustomerNameForServiceMode = useCallback(
    (activeServiceMode: ServiceMode | null | undefined) =>
      getPriceOrderCustomerName({
        serviceMode: activeServiceMode,
        userFullName: user?.details?.name,
        vehicleDescription: vehicleDescription.value,
        isFastLaneEnabled,
      }),
    [isFastLaneEnabled, user?.details?.name, vehicleDescription.value]
  );

  const canPriceLegacyCheckoutOrder = useCallback(
    (mode: ServiceMode) => {
      const activeServiceMode = getServiceModeFromLS() ?? mode;
      const shouldAvoidPricing =
        isDelivery(activeServiceMode) && !isPhoneNumberValidSimple(orderPhoneNumber);

      return Boolean(
        !enableMinimalCartUi &&
          !shouldAvoidPricing &&
          isStoreOpenAndAvailable &&
          user?.details?.phoneNumber &&
          !isLegacyCartTotalNegative &&
          activeServiceMode &&
          legacyPricedCartEntries.length > 0 &&
          allPricingDependenciesAreSettled
      );
    },
    [
      allPricingDependenciesAreSettled,
      enableMinimalCartUi,
      isLegacyCartTotalNegative,
      isStoreOpenAndAvailable,
      legacyPricedCartEntries.length,
      orderPhoneNumber,
      user?.details?.phoneNumber,
    ]
  );

  const startLegacyCheckoutPricing = useCallback(
    (mode: ServiceMode) => {
      const activeServiceMode = getServiceModeFromLS() ?? mode;
      const customerPhoneNumber = user?.details?.phoneNumber;

      if (!customerPhoneNumber || !canPriceLegacyCheckoutOrder(activeServiceMode)) {
        return false;
      }

      syncStorage.remove(SyncStorageKey.ORDER_ID);
      setIsLegacyRepricing();
      setCheckoutRequestedSubtotalCents(legacyRequestedAmountCents);

      priceLegacyOrder({
        rewardsApplied,
        customerLocale,
        customerName: getCustomerNameForServiceMode(activeServiceMode),
        customerPhoneNumber,
        deliveryAddress: deliveryAddress
          ? {
              ...deliveryAddress,
              phoneNumber: orderPhoneNumber,
            }
          : null,
        orderPhoneNumber,
        deliveryInstructions,
        appliedOffers: validAppliedOffers,
        requestedAmountCents: legacyRequestedAmountCents,
        serviceMode: activeServiceMode,
        store: legacyPriceOrderStoreParam,
        isStoreOpenAndAvailable,
      });

      dispatch(actions.loyalty.setIsPricingRewardApplication(false));
      return true;
    },
    [
      canPriceLegacyCheckoutOrder,
      customerLocale,
      deliveryAddress,
      deliveryInstructions,
      dispatch,
      getCustomerNameForServiceMode,
      isStoreOpenAndAvailable,
      legacyPriceOrderStoreParam,
      legacyRequestedAmountCents,
      orderPhoneNumber,
      priceLegacyOrder,
      rewardsApplied,
      setCheckoutRequestedSubtotalCents,
      setIsLegacyRepricing,
      user?.details?.phoneNumber,
      validAppliedOffers,
    ]
  );

  const queueCheckoutReprice = useCallback(
    (mode: ServiceMode, waitForStoreReprice = false) => {
      const hasCartEntries = enableMinimalCartUi
        ? minimalCartEntries.length > 0
        : legacyPricedCartEntries.length > 0;

      if (!mode || !serverOrder?.rbiOrderId || !hasCartEntries) {
        return false;
      }

      setPendingCheckoutReprice({
        mode,
        needsLocalReprice: enableMinimalCartUi,
        waitForStoreReprice: enableMinimalCartUi && waitForStoreReprice,
      });
      return true;
    },
    [
      enableMinimalCartUi,
      legacyPricedCartEntries.length,
      minimalCartEntries.length,
      serverOrder?.rbiOrderId,
    ]
  );

  const priceCheckoutOrderForServiceMode = useCallback(
    (mode: ServiceMode) => {
      return queueCheckoutReprice(mode);
    },
    [queueCheckoutReprice]
  );

  const priceCheckoutOrderForStoreChange = useCallback(
    (mode: ServiceMode) => {
      return queueCheckoutReprice(mode, true);
    },
    [queueCheckoutReprice]
  );

  useEffect(() => {
    if (!pendingCheckoutReprice) {
      return;
    }

    if (!enableMinimalCartUi) {
      if (startLegacyCheckoutPricing(pendingCheckoutReprice.mode)) {
        setPendingCheckoutReprice(null);
      }
      return;
    }

    if (pendingCheckoutReprice.waitForStoreReprice && !isStoreMenuReady) {
      return;
    }

    if (pendingCheckoutReprice.needsLocalReprice) {
      repriceMinimalCartEntries();
      setPendingCheckoutReprice(current =>
        current
          ? {
              ...current,
              needsLocalReprice: false,
            }
          : current
      );
      return;
    }

    setCheckoutRequestedSubtotalCents(calculateCartSubTotal(minimalCartEntries));
    startMinimalCartPricing();
    setPendingCheckoutReprice(null);
  }, [
    enableMinimalCartUi,
    currentStoreNumber,
    indexedMenu,
    isStoreMenuReady,
    isUnifiedMenuLoading,
    loadedStoreNumber,
    minimalCartEntries,
    pendingCheckoutReprice,
    repriceMinimalCartEntries,
    setCheckoutRequestedSubtotalCents,
    startLegacyCheckoutPricing,
    startMinimalCartPricing,
  ]);

  return { priceCheckoutOrderForServiceMode, priceCheckoutOrderForStoreChange } as const;
};
